import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  As400ViewGroupSchema,
  CATALOG_FILTER_MATCH_DEFAULT,
  CatalogAdvancedFilterSchema,
  CatalogFilterMatchSchema,
  CatalogIdentifierFieldSchema,
  CatalogLocationSchema,
  ItemCatalogFilterSchema,
  type ItemCatalogSearchMode,
} from '@erp/rpc';
import { AS400_VIEW_GROUP_ORDER } from './as400Groups';
import { MAX_CATALOG_ITEM_NUMBERS } from './catalogItemNumbers';

// Where the item catalog's search state is parked while the user is elsewhere in
// the app. Session storage (not local storage): the query is remembered for as
// long as the tab lives, so navigating away from /item-master/catalog and back
// doesn't drop it, but it doesn't follow the user into tomorrow — or leak to the
// next user of a shared machine.
const STORAGE_KEY = 'im.catalog.query.v1';

// Where the products ticked for staging are parked, under the same session
// lifetime as the query above — a selection is built up across several searches,
// so it has to survive leaving the page as the query does.
//
// Its own key rather than another field on the query snapshot: the snapshot's
// identity is what decides "is this still the default view?" (and so whether the
// query's Reset button shows at all), and a selection is not part of that
// question. Keeping them apart also means a large selection that storage refuses
// can't take the query snapshot down with it.
const SELECTION_STORAGE_KEY = 'im.catalog.selection.v1';

// The catalog's default page size — the pager's starting rows-per-page.
const DEFAULT_PAGE_SIZE = 25;

// The catalog's search modes and sort directions, pinned to the contract's own
// unions: if one is dropped there, this stops compiling instead of persisting a
// value the backend no longer accepts.
type CatalogSortDirection = NonNullable<
  z.infer<typeof ItemCatalogFilterSchema>['sortDirection']
>;
const SEARCH_MODES = [
  'text',
  'regex',
] as const satisfies readonly ItemCatalogSearchMode[];
const SORT_DIRECTIONS = [
  'asc',
  'desc',
] as const satisfies readonly CatalogSortDirection[];

/**
 * The catalog's *query* state: everything that changes which products the search
 * returns. `CatalogSearchTab`'s filter reducer holds exactly this type, so a new
 * search option can't be added to the page without also being persisted (and
 * vice-versa).
 */
export const CatalogQueryFiltersSchema = z.object({
  search: z.string(),
  searchMode: z.enum(SEARCH_MODES),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  sortDirection: z.enum(SORT_DIRECTIONS),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20),
  // Whether those filters have to be met by one source record or by the product
  // across all of its records. `.default('row')`, so snapshots stored before
  // this field existed restore to the behaviour they were saved under.
  advancedFilterMatch: CatalogFilterMatchSchema.default(
    CATALOG_FILTER_MATCH_DEFAULT,
  ),
  // The multiple-items filter's raw pasted values. The values are what's
  // remembered — a restore re-runs the resolution, so the keys always reflect
  // the catalog as it is now. `.default([])`, so snapshots stored before this
  // field existed still restore instead of being dropped.
  itemNumbers: z.array(z.string()).max(MAX_CATALOG_ITEM_NUMBERS).default([]),
  // Which identifier those pasted values are — Rexel item numbers, supplier
  // part numbers or UPCs. `.default('itemNumber')`, the only kind a snapshot
  // stored before this field existed can hold.
  itemNumberField: CatalogIdentifierFieldSchema.default('itemNumber'),
  // The Source filter: the divisions (DataSources) whose records count. Empty =
  // every division, which is what keeps an unfiltered catalog at its defaults
  // (a list of every id would read as a filter the user never set). Ids only —
  // a restore re-reads the org's divisions, and one that has gone away simply
  // matches nothing.
  dataSourceIds: z.array(z.string()).max(50).default([]),
  // The Location filter: location codes (or whole CDB keys) a record may be
  // keyed by. Empty = every location. `.default([])` on both, so snapshots
  // stored before these fields existed still restore.
  locations: z.array(CatalogLocationSchema).max(50).default([]),
  // The item-list filter: scope the catalog to one org-shared list's products.
  // Only the id is stored — a restore re-reads the list, and a deleted list
  // surfaces as a notice that clears the filter. Mutually exclusive with
  // `itemNumbers` (both are "explicit set" scopes; the reducer keeps at most
  // one engaged). `.default(null)` so older snapshots still restore.
  itemListId: z.string().nullable().default(null),
});
export type CatalogQueryFilters = z.infer<typeof CatalogQueryFiltersSchema>;

/**
 * The catalog's *view* state: which display view renders the result set and how
 * its columns are narrowed. Persisted alongside the query so a restored catalog
 * looks like the one the user left, not just returns the same rows.
 */
export const CatalogViewStateSchema = z.object({
  // "gently" = expandable product rows; "as400" = flattened worksheet grid.
  catalogView: z.enum(['gently', 'as400']),
  // Gently view: multi-select column-group narrowing of the expanded rows. Held
  // as a Set in the page (membership tests); an array here, since a Set has no
  // JSON representation.
  as400Groups: z.array(As400ViewGroupSchema),
  // AS/400 view: the single group whose columns the flat grid shows.
  as400ViewGroup: As400ViewGroupSchema,
  // Gently view: the column order the reader dragged each raw schema's table
  // into, keyed by schema definition name — `{ as_item: ['ICOMP', 'IDLINE', …] }`.
  // Kept per schema rather than per expanded row, so a product's `as_item` table
  // is arranged like the last one the reader arranged. A schema with no entry
  // (and every schema, until a header is dragged) renders its natural order.
  // `.default({})`, so snapshots stored before this field existed still restore.
  rawColumnOrder: z.record(z.string(), z.array(z.string())).default({}),
  // Gently view: the raw columns the reader hid per raw schema, keyed the same
  // way as `rawColumnOrder` — `{ as_item: ['IMDESC', 'IMXCLASS'] }`. Held as the
  // hidden ids rather than the shown ones on purpose: a schema that gains a
  // field shows it, where a stored list of *shown* columns would silently
  // swallow every column added after it was written. `.default({})`, so
  // snapshots stored before this field existed still restore.
  rawColumnHidden: z.record(z.string(), z.array(z.string())).default({}),
  // Whether the advanced-filters panel is expanded.
  filtersOpen: z.boolean(),
});
export type CatalogViewState = z.infer<typeof CatalogViewStateSchema>;

/**
 * One session's worth of item catalog search state — the query plus the view it
 * was seen through. This is the single declaration of the catalog's state shape:
 * the page's two state hooks are typed from the halves above, and the snapshot is
 * built by spreading them, so adding an option to either schema is a compile
 * error until the page stores and restores it.
 */
export const CatalogQuerySnapshotSchema = z.object({
  ...CatalogQueryFiltersSchema.shape,
  ...CatalogViewStateSchema.shape,
});
export type CatalogQuerySnapshot = z.infer<typeof CatalogQuerySnapshotSchema>;

/** Which display view the search tab renders. */
export type CatalogView = CatalogQuerySnapshot['catalogView'];

/** The state a fresh catalog opens in — and what Reset returns it to. */
export const DEFAULT_CATALOG_QUERY: CatalogQuerySnapshot = {
  search: '',
  searchMode: 'text',
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sortDirection: 'asc',
  advancedFilters: [],
  advancedFilterMatch: CATALOG_FILTER_MATCH_DEFAULT,
  itemNumbers: [],
  itemNumberField: 'itemNumber',
  itemListId: null,
  dataSourceIds: [],
  locations: [],
  catalogView: 'gently',
  as400Groups: [],
  as400ViewGroup: AS400_VIEW_GROUP_ORDER[0],
  rawColumnOrder: {},
  rawColumnHidden: {},
  filtersOpen: false,
};

/**
 * The snapshot with its page number dropped back to the first.
 *
 * The page is the one piece of the query deliberately *not* carried across a
 * navigation: it only means anything against the result set it was counted from.
 * The catalog changes under a session, and the AS/400 view expands the same
 * product page into a different number of rows — so a remembered page number can
 * land past the end of what's there now, which surfaces as an empty list with
 * nothing on screen to explain it (in the AS/400 view the pager is driven by the
 * Gently list's page info, so it can't even show the mismatch). The filters are
 * what's worth remembering; results reopen at the first page.
 */
function atFirstPage(snapshot: CatalogQuerySnapshot): CatalogQuerySnapshot {
  return { ...snapshot, page: DEFAULT_CATALOG_QUERY.page };
}

/**
 * A snapshot split into the page's two state halves — the filter reducer's state
 * and the view-state hook's. Parsed rather than hand-picked, so the split follows
 * the schemas as they grow.
 */
export function splitCatalogQuery(snapshot: CatalogQuerySnapshot): {
  filters: CatalogQueryFilters;
  viewState: CatalogViewState;
} {
  return {
    filters: CatalogQueryFiltersSchema.parse(snapshot),
    viewState: CatalogViewStateSchema.parse(snapshot),
  };
}

const DEFAULT_HALVES = splitCatalogQuery(DEFAULT_CATALOG_QUERY);

/** The filter reducer's starting state — and what Reset returns it to. */
export const DEFAULT_CATALOG_FILTERS = DEFAULT_HALVES.filters;

/** The view state's starting value — and what Reset returns it to. */
export const DEFAULT_CATALOG_VIEW_STATE = DEFAULT_HALVES.viewState;

// Key order of the schema's fields, for an order-independent snapshot identity
// (`JSON.stringify` on the object itself would depend on literal key order,
// which would quietly break the "is this still the default view?" comparison).
const SNAPSHOT_KEYS = Object.keys(
  CatalogQuerySnapshotSchema.shape,
).sort() as (keyof CatalogQuerySnapshot)[];

function snapshotIdentity(snapshot: CatalogQuerySnapshot): string {
  return JSON.stringify(SNAPSHOT_KEYS.map((key) => snapshot[key]));
}

const DEFAULT_IDENTITY = snapshotIdentity(DEFAULT_CATALOG_QUERY);

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

// ─── Session storage access ───────────────────────────────────────────────────
// All three are best-effort: storage can be unavailable (private-mode Safari,
// blocked storage) or full, which just means the catalog opens at its defaults.
// Exported for the review page's view state (reviewViewSession), which parks
// itself the same way.

export function readKey(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function clearKey(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing was stored to begin with.
  }
}

export function writeKey(key: string, serialized: string): void {
  try {
    window.sessionStorage.setItem(key, serialized);
  } catch {
    // No restore, but the live state is unaffected.
  }
}

function readSnapshot(): CatalogQuerySnapshot {
  const raw = readKey(STORAGE_KEY);
  if (!raw) return DEFAULT_CATALOG_QUERY;

  const parsed = CatalogQuerySnapshotSchema.safeParse(parseJson(raw));
  if (!parsed.success) {
    // A snapshot written by an older shape of the schema (or hand-edited).
    // Drop it rather than half-restoring a query.
    clearKey(STORAGE_KEY);
    return DEFAULT_CATALOG_QUERY;
  }
  // Also normalized on the way out, so a snapshot stored before this rule
  // (or by an older build) can't reopen on a page that isn't there.
  return atFirstPage(parsed.data);
}

function writeSnapshot(identity: string, serialized: string): void {
  // A default view has nothing worth restoring, so drop the key instead of
  // storing a no-op snapshot. This is also what makes Reset clear the session.
  if (identity === DEFAULT_IDENTITY) {
    clearKey(STORAGE_KEY);
    return;
  }
  writeKey(STORAGE_KEY, serialized);
}

/**
 * The catalog's *staging selection*: the products ticked for staging, plus the
 * header select-all flag ("every product matching the current filters" —
 * meaningful only against the query stored alongside it, which is restored with
 * it). `CatalogSearchTab`'s selection hook holds exactly this, as a Set.
 */
export const CatalogSelectionStateSchema = z.object({
  selectedKeys: z.array(z.string()),
  allSelected: z.boolean(),
});
export type CatalogSelectionState = z.infer<typeof CatalogSelectionStateSchema>;

/** The selection a fresh catalog opens with: nothing ticked. */
export const DEFAULT_CATALOG_SELECTION: CatalogSelectionState = {
  selectedKeys: [],
  allSelected: false,
};

function isEmptySelection(selection: CatalogSelectionState): boolean {
  return !selection.allSelected && selection.selectedKeys.length === 0;
}

function readSelection(): CatalogSelectionState {
  const raw = readKey(SELECTION_STORAGE_KEY);
  if (!raw) return DEFAULT_CATALOG_SELECTION;

  const parsed = CatalogSelectionStateSchema.safeParse(parseJson(raw));
  if (!parsed.success) {
    clearKey(SELECTION_STORAGE_KEY);
    return DEFAULT_CATALOG_SELECTION;
  }
  return parsed.data;
}

/**
 * The state to seed the catalog with — whatever the user left behind earlier in
 * this browser session, or the defaults — already split into the page's two state
 * halves. Read once, synchronously, on the first render, so the very first search
 * request carries the restored query instead of firing an empty search and
 * refetching.
 */
export function useRestoredCatalogQuery(): ReturnType<
  typeof splitCatalogQuery
> {
  const [restored] = useState(() => splitCatalogQuery(readSnapshot()));
  return restored;
}

/**
 * Mirrors the live catalog state into session storage, and reports whether the
 * view is still at its defaults (nothing to reset). Writes only when the
 * snapshot's values actually change — search and filter commits are already
 * debounced upstream, so this settles at roughly one write per user action.
 *
 * Paging alone changes nothing here: the stored snapshot always sits on the first
 * page (see `atFirstPage`), so it neither writes on every page turn nor offers a
 * Reset for one.
 */
export function usePersistedCatalogQuery(snapshot: CatalogQuerySnapshot): {
  isDefault: boolean;
} {
  const stored = atFirstPage(snapshot);
  const identity = snapshotIdentity(stored);
  const serialized = JSON.stringify(stored);

  // Storage is an external system, so mirroring into it belongs in an effect.
  // Both deps derive from the same state, so this runs once per changed value.
  useEffect(() => {
    writeSnapshot(identity, serialized);
  }, [identity, serialized]);

  return { isDefault: identity === DEFAULT_IDENTITY };
}

/**
 * The staging selection to seed the catalog with — whatever was ticked earlier in
 * this browser session, or nothing. Read once on the first render, like the query
 * is, so the restored selection is on screen with the first rows.
 */
export function useRestoredCatalogSelection(): CatalogSelectionState {
  const [restored] = useState(readSelection);
  return restored;
}

/**
 * Mirrors the live staging selection into session storage, so navigating away
 * from the catalog and back keeps the products ticked for staging.
 *
 * An empty selection drops the key rather than storing a no-op — which is also
 * what makes "Reset selected products" (and a completed stage) clear the session.
 */
export function usePersistedCatalogSelection(
  selection: CatalogSelectionState,
): void {
  const isEmpty = isEmptySelection(selection);
  const serialized = JSON.stringify(selection);

  // Storage is an external system, so mirroring into it belongs in an effect.
  useEffect(() => {
    if (isEmpty) clearKey(SELECTION_STORAGE_KEY);
    else writeKey(SELECTION_STORAGE_KEY, serialized);
  }, [isEmpty, serialized]);
}
